/**
 * Field Data Transformer
 *
 * Transforms old field data JSON format to be compatible with the new field factory.
 * This transformer handles mapping of field types and structural changes.
 */

// Field type mappings from old format to new format
const FIELD_TYPE_MAPPINGS: Record<string, string> = {
  // Direct mappings
  user_name: 'user_name',
  text: 'text',
  email: 'email',
  number: 'number',
  phone_number: 'phone_number',
  select: 'select',
  checkbox: 'checkbox',
  textarea: 'textarea',
  datePicker: 'datePicker',
  params: 'params',
  fileUploader: 'fileUploader',
  stateAsyncSelect: 'stateAsyncSelect',
  districtAsyncSelect: 'districtAsyncSelect',
  branchAsyncSelect: 'branchAsyncSelect',
  college: 'college',
  radioGroup: 'radioGroup',
  multiSelectCheckboxGroup: 'multiSelectCheckboxGroup',
  whatsappInfoStatus: 'whatsappInfoStatus',
  conditionallyDependentField: 'conditionallyDependentField',
  slotSelectionField: 'slotSelectionField',
  countryAsyncSelect: 'countryAsyncSelect',
  multipleSelect: 'multipleSelect',
  banner: 'banner',
  content: 'content',
  formFieldGroup: 'formFieldGroup',
  informedOTPField: 'informedOTPField',

  // Special mappings that need transformation
  selectedGender: 'select', // Will be handled as select with gender options
  program: 'text', // Program field becomes a text field
  amount: 'text', // Amount field becomes a text field (readonly)
  inviteCode: 'text', // Invite code becomes a text field
  couponCode: 'text', // Coupon code becomes a text field
  fullName: 'user_name', // Full name field maps to user_name
  phone: 'phone_number', // Phone field maps to phone_number
  name: 'user_name', // Name field maps to user_name
  lastName: 'text', // Last name field maps to text
  currentCity: 'text', // Current city field maps to text
  currentState: 'stateAsyncSelect', // Current state maps to state async select
  currentDistrict: 'districtAsyncSelect', // Current district maps to district async select
  pincode: 'text', // Pincode field maps to text
  country: 'countryAsyncSelect', // Country field maps to country async select
  occupation: 'select', // Occupation field maps to select
  preferredLanguage: 'select', // Preferred language maps to select
  department: 'select', // Department field maps to select
  degree: 'select' // Degree field maps to select
};

// Special field handlers for fields that need additional transformation
const SPECIAL_FIELD_HANDLERS = {
  selectedGender: (field: any) => ({
    ...field,
    type: 'select',
    shouldShowLabel: field.shouldShowLabel !== false, // Default to true unless explicitly false
    options: [
      { label: 'Male', value: 'MALE' },
      { label: 'Female', value: 'FEMALE' },
      { label: 'Other', value: 'OTHER' },
      { label: 'Prefer not to say', value: 'PREFER_NOT_TO_SAY' }
    ]
  }),

  program: (field: any) => ({
    ...field,
    type: 'text',
    category: 'program',
    shouldShowLabel: field.shouldShowLabel !== false, // Default to true unless explicitly false
    isReadOnly: true,
    disabled: true
  }),

  amount: (field: any) => ({
    ...field,
    type: 'text',
    category: 'amount',
    shouldShowLabel: field.shouldShowLabel !== false, // Default to true unless explicitly false
    isReadOnly: true,
    disabled: true
  }),

  inviteCode: (field: any) => ({
    ...field,
    type: 'text',
    category: 'invite_code',
    shouldShowLabel: field.shouldShowLabel !== false, // Default to true unless explicitly false
    placeholder: field.placeholder || 'Enter Invite Code',
    validation_type: 'string'
  }),

  couponCode: (field: any) => ({
    ...field,
    type: 'text',
    category: 'coupon_code',
    shouldShowLabel: field.shouldShowLabel !== false, // Default to true unless explicitly false
    placeholder: field.placeholder || 'Enter Coupon Code',
    validation_type: 'string'
  })
};

interface OldFieldData {
  type: string;
  field: string;
  label: string;
  options?: Array<{ label: string; value: string } | string>;
  validation_type?: string;
  validations?: Array<{
    type: string;
    params: string[];
    error_message?: string;
  }>;
  tags?: string[];
  isRelevant?: boolean;
  relevantCondition?: string;
  is_dependent?: boolean;
  dependent_on_field?: string;
  helper_text?: string;
  placeholder?: string;
  isHidden?: boolean;
  shouldPrefilledFromUserProfile?: boolean;
  regex_type?: string;
  validation_message?: string;
  max_date?: string;
  shouldShowLabel?: boolean;
  s3_upload_bucket_path?: string;
  help_text_below_label?: string;
  placeholder_helper_text?: string;
  fileAcceptTypes?: string;
  aclType?: string;
  search_key?: string;
  dependent_fields?: string[];
  is_tnc?: boolean;
  tnc_enum?: string;
  [key: string]: any;
}

interface TransformedFieldData {
  type: string;
  field: string;
  label: string;
  options?: Array<{ label: string; value: string }>;
  validation_type?: string;
  validations?: Array<{
    type: string;
    params: string[];
    error_message?: string;
  }>;
  tags?: string[];
  isRelevant?: boolean;
  relevantCondition?: string;
  is_dependent?: boolean;
  dependent_on_field?: string;
  helper_text?: string;
  placeholder?: string;
  isHidden?: boolean;
  shouldPrefilledFromUserProfile?: boolean;
  regex_type?: string;
  validation_message?: string;
  max_date?: string;
  shouldShowLabel?: boolean;
  s3_upload_bucket_path?: string;
  help_text_below_label?: string;
  placeholder_helper_text?: string;
  fileAcceptTypes?: string;
  aclType?: string;
  search_key?: string;
  dependent_fields?: string[];
  is_tnc?: boolean;
  tnc_enum?: string;
  isReadOnly?: boolean;
  disabled?: boolean;
  [key: string]: any;
}

/**
 * Transforms a single field from old format to new format
 */
const transformField = (field: OldFieldData): TransformedFieldData => {
  const { type: oldType } = field;

  // Check if field has special handler
  if (SPECIAL_FIELD_HANDLERS[oldType as keyof typeof SPECIAL_FIELD_HANDLERS]) {
    return SPECIAL_FIELD_HANDLERS[
      oldType as keyof typeof SPECIAL_FIELD_HANDLERS
    ](field);
  }

  // Get mapped type or keep original if no mapping exists
  const newType = FIELD_TYPE_MAPPINGS[oldType] || oldType;

  // Transform options array if it exists
  let transformedOptions = field.options;
  if (field.options && Array.isArray(field.options)) {
    transformedOptions = field.options.map((option) => {
      if (typeof option === 'string') {
        return { label: option, value: option };
      }
      return option;
    });
  }

  return {
    ...field,
    type: newType,
    shouldShowLabel: field.shouldShowLabel !== false, // Default to true unless explicitly false
    placeholder:
      field.placeholder || (field.type === 'select' ? '--Select' : undefined),
    ...(transformedOptions && { options: transformedOptions })
  };
};

/**
 * Transforms the entire fields data array from old format to new format
 */
export const transformFieldsData = (
  fieldsData: OldFieldData[]
): TransformedFieldData[] => {
  if (!Array.isArray(fieldsData)) {
    throw new Error('Fields data must be an array');
  }

  return fieldsData.map(transformField);
};

/**
 * Transforms the complete field data JSON structure
 */
export const transformFieldDataJSON = (fieldDataJSON: {
  fields_data: OldFieldData[];
}) => {
  if (!fieldDataJSON || !fieldDataJSON.fields_data) {
    throw new Error(
      'Invalid field data JSON structure. Expected { fields_data: [...] }'
    );
  }

  return {
    fields_data: transformFieldsData(fieldDataJSON.fields_data)
  };
};

/**
 * Validates if a field type is supported by the new field factory
 */
export const isFieldTypeSupported = (fieldType: string): boolean => {
  const supportedTypes = [
    'user_name',
    'text',
    'email',
    'number',
    'phone_number',
    'select',
    'stateAsyncSelect',
    'districtAsyncSelect',
    'informedOTPField',
    'checkbox',
    'whatsappInfoStatus',
    'params',
    'college',
    'radioGroup',
    'slotSelectionField',
    'conditionallyDependentField',
    'multiSelectCheckboxGroup',
    'branchAsyncSelect',
    'banner',
    'multipleSelect',
    'content',
    'formFieldGroup',
    'fileUploader',
    'datePicker',
    'countryAsyncSelect',
    'textarea'
  ];

  return (
    supportedTypes.includes(fieldType) ||
    FIELD_TYPE_MAPPINGS[fieldType] !== undefined
  );
};

/**
 * Gets unsupported field types from the fields data
 */
export const getUnsupportedFieldTypes = (
  fieldsData: OldFieldData[]
): string[] => {
  const unsupportedTypes = new Set<string>();

  fieldsData.forEach((field) => {
    const mappedType = FIELD_TYPE_MAPPINGS[field.type] || field.type;
    if (!isFieldTypeSupported(field.type)) {
      unsupportedTypes.add(field.type);
    }
  });

  return Array.from(unsupportedTypes);
};

/**
 * Transforms old SingleStepFormTypes parsedUserExtraInfoConfig to new MultiStepFormFieldsDataType
 * Handles complete field conversion for new field factory compatibility
 */
export const transformUserExtraInfoConfig = (oldConfig: any) => {
  // Handle undefined, null, or already transformed input
  if (!oldConfig || oldConfig.registration_form_details) {
    return (
      oldConfig || {
        registration_form_details: {
          initial_step_id: 'default_step',
          step_details: []
        },
        request_object_extra_fields_data: [],
        form_type: 'MULTI_STEP_PRODUCT_REGISTRATION'
      }
    );
  }

  // Extract and transform fields_data from old format
  const fieldsData = oldConfig.fields_data || [];
  const transformedFields = fieldsData.map(transformField);

  // Generate request_object_extra_fields_data from transformed fields
  const requestObjectExtraFieldsData = transformedFields.map((field) => ({
    field: field.field,
    tags: field.tags || ['NON_PII'],
    field_name_in_request_object: field.field
  }));

  // Return new MultiStepFormFieldsDataType structure
  return {
    registration_form_details: {
      initial_step_id: 'default_step',
      step_details: [
        {
          step_id: 'default_step',
          step_type: 'DEFAULT',
          fields_data: transformedFields
        }
      ]
    },
    request_object_extra_fields_data: requestObjectExtraFieldsData,
    form_type: 'MULTI_STEP_PRODUCT_REGISTRATION'
  };
};

export default {
  transformFieldsData,
  transformFieldDataJSON,
  isFieldTypeSupported,
  getUnsupportedFieldTypes,
  transformUserExtraInfoConfig
};
